export type Point2d = {
    x: number;
    y: number;
};

export type Edge2d = {
    p1: Point2d;
    p2: Point2d;
};

export type Rect2d = {
    x: number;
    y: number;
    w: number;
    h: number;
};

export default class Geom2d {
    /** Returns the distance between the two points represented by the given x and y coordinates.
     * @param x1 X-coordinate of the first point.
     * @param y1 Y-coordinate of the first point.
     * @param x2 X-coordinate of the second point.
     * @param y2 Y-coordinate of the second point.
     */
    public static distanceBetween(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
    ): number {
        return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }

    /** Returns true if, and only if, two axis-aligned rectangles formed by the given points have any overlap.
     * @param x1 X-coordinate of the left edge of the first rectangle.
     * @param y1 Y-coordinate of the top edge of the first rectangle.
     * @param x2 X-coordinate of the right edge of the first rectangle.
     * @param y2 Y-coordinate of the bottom edge of the first rectangle.
     * @param x3 X-coordinate of the left edge of the second rectangle.
     * @param y3 Y-coordinate of the top edge of the second rectangle.
     * @param x4 X-coordinate of the right edge of the second rectangle.
     * @param y4 Y-coordinate of the bottom edge of the second rectangle.
     */
    public static hasAABBOverlap(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
    ): boolean {
        return !(
            (x1 > x3 && x1 > x4 && x2 > x3 && x2 > x4) ||
            (x1 < x3 && x1 < x4 && x2 < x3 && x2 < x4) ||
            (y1 > y3 && y1 > y4 && y2 > y3 && y2 > y4) ||
            (y1 < y3 && y1 < y4 && y2 < y3 && y2 < y4)
        );
    }

    /** Returns the slope between two lines defined by the given points.
     * @param x1 X-coordinate of the first point of the line.
     * @param y1 Y-coordinate of the first point of the line.
     * @param x2 X-coordinate of the second point of the line.
     * @param y2 Y-coordinate of the second point of the line.
     */
    public static slope(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
    ): number {
        if (y1 === y2) {
            return Number.POSITIVE_INFINITY;
        }

        return (y2 - y1) / (x2 - x1);
    }

    /** Returns the (possibly null) x-intercept of the line represented by the given points.
     * @param x1 X-coordinate of the first point of the line.
     * @param y1 Y-coordinate of the first point of the line.
     * @param x2 X-coordinate of the second point of the line.
     * @param y2 Y-coordinate of the second point of the line.
     */
    public static xIntercept(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
    ): number | null {
        if (x1 === x2) {
            return x1;
        }

        if (y1 === y2) {
            return y1 === 0 ? 0 : null;
        }

        const slope = this.slope(x1, y1, x2, y2);

        return x1 - y1 / slope;
    }

    /** Returns the (possibly null) y-intercept of the line represented by the given points.
     * @param x1 X-coordinate of the first point of the line.
     * @param y1 Y-coordinate of the first point of the line.
     * @param x2 X-coordinate of the second point of the line.
     * @param y2 Y-coordinate of the second point of the line.
     */
    public static yIntercept(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
    ): number | null {
        if (y1 === y2) {
            return y1;
        }

        if (x1 === x2) {
            return x1 === 0 ? 0 : null;
        }

        const slope = this.slope(x1, y1, x2, y2);

        return -slope * x1 + y1;
    }

    /** Returns true if the lines represented by the given points are parallel lines (and possibly collinear).
     * @param x1 X-coordinate of the first point of the first line.
     * @param y1 Y-coordinate of the first point of the first line.
     * @param x2 X-coordinate of the second point of the first line.
     * @param y2 Y-coordinate of the second point of the first line.
     * @param x3 X-coordinate of the first point of the second line.
     * @param y3 Y-coordinate of the first point of the second line.
     * @param x4 X-coordinate of the second point of the second line.
     * @param y4 Y-coordinate of the second point of the second line.
     */
    public static parallel(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
    ): boolean {
        return this.slope(x1, y1, x2, y2) === this.slope(x3, y3, x4, y4);
    }

    /** Returns true if the lines represented by the given points are collinear.
     * @param x1 X-coordinate of the first point of the first line.
     * @param y1 Y-coordinate of the first point of the first line.
     * @param x2 X-coordinate of the second point of the first line.
     * @param y2 Y-coordinate of the second point of the first line.
     * @param x3 X-coordinate of the first point of the second line.
     * @param y3 Y-coordinate of the first point of the second line.
     * @param x4 X-coordinate of the second point of the second line.
     * @param y4 Y-coordinate of the second point of the second line.
     */
    public static collinear(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
    ): boolean {
        if (this.parallel(x1, y1, x2, y2, x3, y3, x4, y4)) {
            return (
                this.xIntercept(x1, y1, x2, y2) ===
                this.xIntercept(x3, y3, x4, y4)
            );
        }

        return false;
    }

    /** Returns the point of intersection point of two line segments represented by the given points, or null if they do not intersect.
     * @param x1 X-coordinate of the first point of the first line.
     * @param y1 Y-coordinate of the first point of the first line.
     * @param x2 X-coordinate of the second point of the first line.
     * @param y2 Y-coordinate of the second point of the first line.
     * @param x3 X-coordinate of the first point of the second line.
     * @param y3 Y-coordinate of the first point of the second line.
     * @param x4 X-coordinate of the second point of the second line.
     * @param y4 Y-coordinate of the second point of the second line.
     */
    public static intersection(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        x3: number,
        y3: number,
        x4: number,
        y4: number,
    ): Point2d | null {
        if (!this.hasAABBOverlap(x1, y1, x2, y2, x3, y3, x4, y4)) {
            return null;
        }

        if (this.parallel(x1, y2, x2, y2, x3, y3, x4, y4)) {
            if (this.collinear(x1, y1, x2, y2, x3, y3, x4, y4)) {
                const xMids = [x1, x2, x3, x4]
                    .sort((a, b) => a - b)
                    .slice(1, 3);
                const yMids = [y1, y2, y3, y4]
                    .sort((a, b) => a - b)
                    .slice(1, 3);

                return {
                    x: (xMids[0] + xMids[1]) / 2,
                    y: (yMids[0] + yMids[1]) / 2,
                };
            }

            return null;
        }

        const xIntersection =
            ((x1 * y2 - y1 * x2) * (x3 - x4) -
                (x1 - x2) * (x3 * y4 - y3 * x4)) /
            ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));

        const yIntersection =
            ((x1 * y2 - y1 * x2) * (y3 - y4) -
                (y1 - y2) * (x3 * y4 - y3 * x4)) /
            ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));

        const xInRange =
            (x2 > x1
                ? xIntersection >= x1 && xIntersection <= x2
                : xIntersection >= x2 && xIntersection <= x1) &&
            (x4 > x3
                ? xIntersection >= x3 && xIntersection <= x4
                : xIntersection >= x4 && xIntersection <= x3);
        const yInRange =
            (y2 > y1
                ? yIntersection >= y1 && yIntersection <= y2
                : yIntersection >= y2 && yIntersection <= y1) &&
            (y4 > y3
                ? yIntersection >= y3 && yIntersection <= y4
                : yIntersection >= y4 && yIntersection <= y3);

        return xInRange && yInRange
            ? { x: xIntersection, y: yIntersection }
            : null;
    }

    /** For the line segment represented by the given points, returns the any intersections with edges in the provided edges array.
     * @param x1 X-coordinate of the first point of the test line.
     * @param y1 Y-coordinate of the first point of the test line.
     * @param x2 X-coordinate of the second point of the test line.
     * @param y2 Y-coordinate of the second point of the test line.
     * @param edges Array of edges to check for intersections against the test line.
     */
    public static intersections(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        ...edges: Edge2d[]
    ): Array<Point2d> {
        return edges.flatMap(
            (edge) =>
                this.intersection(
                    x1,
                    y1,
                    x2,
                    y2,
                    edge.p1.x,
                    edge.p1.y,
                    edge.p2.x,
                    edge.p2.y,
                ) ?? [],
        );
    }

    /** For the line segment represented by the given points, returns the edges in the edge array that is intersects.
     * @param x1 X-coordinate of the first point of the test line.
     * @param y1 Y-coordinate of the first point of the test line.
     * @param x2 X-coordinate of the second point of the test line.
     * @param y2 Y-coordinate of the second point of the test line.
     * @param edges Array of edges to check for intersections against the test line.
     */
    public static intersectionIndices(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        ...edges: Edge2d[]
    ): Array<number> {
        const result: Array<number> = [];

        for (let i = 0; i < edges.length; ++i) {
            if (
                this.intersection(
                    x1,
                    y1,
                    x2,
                    y2,
                    edges[i].p1.x,
                    edges[i].p1.y,
                    edges[i].p2.x,
                    edges[i].p2.y,
                )
            ) {
                result.push(i);
            }
        }

        return result;
    }

    /** For the line segment represented by the given points, returns the number of edges in the edge array that is intersects.
     * @param x1 X-coordinate of the first point of the test line.
     * @param y1 Y-coordinate of the first point of the test line.
     * @param x2 X-coordinate of the second point of the test line.
     * @param y2 Y-coordinate of the second point of the test line.
     * @param edges Array of edges to check for intersections against the test line.
     */
    public static intersectionCount(
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        ...edges: Edge2d[]
    ): number {
        return edges.filter(
            (edge) =>
                this.intersection(
                    x1,
                    y1,
                    x2,
                    y2,
                    edge.p1.x,
                    edge.p1.y,
                    edge.p2.x,
                    edge.p2.y,
                ) != null,
        ).length;
    }
}
